
import { Component, Vue, Prop, Watch, Emit, Ref } from 'vue-property-decorator'
import { Debounce, Bind } from 'lodash-decorators'
import lodash from 'lodash'

type WaterfallItem = {
  id: string | number
  height: number
  image?: string
  [key: string]: any
}

type VisibleItems = WaterfallItem &
  ({ top: number; y?: never } | { top?: never; y: number }) &
  ({ left: number; x?: never } | { left?: never; x: number })

@Component({
  components: {}
})
export default class VirtualWaterfall extends Vue {
  @Prop({ default: () => [] }) list: WaterfallItem[]
  @Prop({ required: true }) cols: number
  @Prop({ required: true }) itemWidth: number
  @Prop({ default: 20 }) gap: number | number[]

  @Prop() bufferSize: number

  @Ref()
  wrapperEl: HTMLElement | null

  columnHeights: number[] = []
  itemPositions: Record<string, VisibleItems> = {}

  scrollTop: number = 0
  scrollRafId: number | null = null
  containerOffsetTop: number = 0 // 新增：记录容器顶部偏移

  /** 虚拟列表动态缓存区
   * 最大缓冲区不超过视口高度的1.5倍，最小为视口高度的1倍。其他情况下根据内容高度的1/4动态调整
   */
  get optimalBufferSize(): number {
    if (this.bufferSize) return this.bufferSize

    const height = this.maxColumnHeight
    const viewportHeight = window.innerHeight
    const baseBuffer = viewportHeight * 1.5

    return Math.min(baseBuffer, Math.max(height / 4, viewportHeight))
  }

  get maxColumnHeight() {
    return Math.max(...this.columnHeights, 0)
  }

  get visibleItems(): Array<VisibleItems> {
    const startY = this.scrollTop - this.optimalBufferSize
    const endY = this.scrollTop + window.innerHeight + this.optimalBufferSize

    return Object.values(this.itemPositions)
      .map(item => {
        const top = item.y ?? item.top
        const itemBottom = top + item.height
        const isVisible = itemBottom > startY && top < endY

        // 返回所有项目，标记可见性
        return {
          ...item,
          isVisible
        }
      })
      .sort((a, b) => (a.y ?? a.top) - (b.y ?? b.top))
  }

  /**
   * 计算布局
   * 使用贪心算法将项目放在最短的列中
   */
  calculateLayout() {
    // 重置列高度
    this.columnHeights = new Array(this.cols).fill(0)
    this.itemPositions = {}

    this.list.forEach(item => {
      // 找到最短的列
      const columnIndex = this.columnHeights.indexOf(Math.min(...this.columnHeights))
      const [colGap, rowGap] = Array.isArray(this.gap) ? this.gap : [this.gap, this.gap]
      const top = this.columnHeights[columnIndex]
      const left = columnIndex * (this.itemWidth + colGap)

      const tempItem = lodash.cloneDeep(item)
      tempItem[!lodash.has(item, 'left') ? 'left' : 'x'] = left
      tempItem[!lodash.has(item, 'top') ? 'top' : 'y'] = top

      // 判断是否有重复id，如果有输出日志
      // if (lodash.has(this.itemPositions, item.id)) {
      //   console.log('calculateLayout', item.id)
      // }

      this.itemPositions[item.id] = tempItem as VisibleItems
      // 更新该列的高度
      this.columnHeights[columnIndex] += tempItem.height + rowGap
    })
  }

  getItemStyle(item) {
    const styles: string[] = []

    styles.push(`position: absolute`)
    styles.push(`left: ${item.x ?? item.left}px`)
    styles.push(`top: ${item.y ?? item.top}px`)
    styles.push(`width: ${this.itemWidth}px`)
    styles.push(`height: ${item.height}px`)
    styles.push(`transition: left 0.3s ease, top 0.3s ease`)

    if (!item.isVisible) {
      styles.push(`display: none`)
    }

    return styles.join(';')
  }

  /** 监听滚动事件，更新 scrollTop 的值
   *  计算容器顶部相对于视口的位置，当容器已滚出视口时，scrollTop应该是容器高度 + 已滚动的距离
   */
  @Debounce(50, { leading: true, trailing: true })
  @Bind()
  onWindowScroll() {
    if (this.scrollRafId) {
      cancelAnimationFrame(this.scrollRafId)
    }
    this.scrollRafId = requestAnimationFrame(() => {
      const rect = this.wrapperEl?.getBoundingClientRect()
      if (rect) {
        const containerTop = rect.top
        this.scrollTop = Math.max(0, -containerTop)
      }
    })
  }

  @Watch('list')
  updateList() {
    this.calculateLayout()
  }

  created(): void {}

  mounted(): void {
    this.$nextTick(() => {
      this.containerOffsetTop = this.wrapperEl?.offsetTop || 0
    })
    window.addEventListener('scroll', this.onWindowScroll, false)
  }

  updated(): void {}

  destroyed(): void {
    window.removeEventListener('scroll', this.onWindowScroll, false)

    if (this.scrollRafId) {
      cancelAnimationFrame(this.scrollRafId)
    }
  }
}
